/**
 * Authentication Context Provider using Azure AD (MSAL)
 * 
 * Provides user authentication for login/logout only. Backend APIs use 
 * email-based authentication without requiring JWT tokens.
 * 
 * Features:
 * - User login/logout via Azure AD
 * - User profile access (name, email)
 * - No backend API token management
 */

import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import {
    useIsAuthenticated,
    useMsal,
    useAccount
} from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../config/authConfig';
import { AccountInfo } from '@azure/msal-browser';
import { authManager } from '../services/authManager';
import { setMsalInstance } from '../services/authenticatedHttpClient';
import { BYPASS_AUTH, DEV_EMAIL, SESSION_TIMEOUT_MS } from '../config/env';
import { fetchUserGroups, UserGroup, UserGroupsServiceResult } from '../services/userGroupsService';

/** Events that count as user activity */
const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
    'mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'
];

// User profile interface
export interface UserProfile {
    id: string;
    name: string;
    email: string;
    roles?: string[];
    tenantId?: string;
    userGroups?: UserGroup[];
    groupNamesCommaSeparated?: string;
}

// Authentication context interface
interface AuthContextType {
    // Authentication state
    isAuthenticated: boolean;
    isLoading: boolean;
    user: UserProfile | null;

    // Authentication methods
    login: () => Promise<void>;
    logout: () => Promise<void>;

    // Utility methods
    getUserEmail: () => string | null;

    // Error state
    error: string | null;
    clearError: () => void;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
interface AuthProviderProps {
    children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const account = useAccount(accounts[0] || {});

    const [user, setUser] = useState<UserProfile | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [manualLogout, setManualLogout] = useState(false); // Track manual logout
    // Dev bypass flag now centralized in env.ts (only active when env var exists AND equals "true").

    // Guard: only fetch user groups once per email to avoid duplicate API calls
    const groupsFetchedForRef = useRef<string | null>(null);

    // Convert MSAL account to our user profile
    const convertAccountToUserProfile = (account: AccountInfo): UserProfile => {
        // Use email address as user ID for backend
        const userId = account.username; // This is the email address

        return {
            id: userId,
            name: account.name || account.username,
            email: account.username,
            tenantId: account.tenantId,
            // Extract roles from ID token claims if available
            roles: (account.idTokenClaims as any)?.roles || [],
            userGroups: [], // Will be populated asynchronously
            groupNamesCommaSeparated: '' // Will be populated asynchronously
        };
    };

    // Fetch user's groups from backend API (guarded to run once per email)
    const fetchAndSetUserGroups = async (userEmail: string) => {
        if (groupsFetchedForRef.current === userEmail) return;
        groupsFetchedForRef.current = userEmail;
        try {
            const result = await fetchUserGroups(userEmail);

            // Store groups in authManager for service layer access
            authManager.setUserGroups(result.groupNamesCommaSeparated);

            // Update user profile with user groups and comma-separated names
            setUser(prev => prev ? {
                ...prev,
                userGroups: result.groups,
                groupNamesCommaSeparated: result.groupNamesCommaSeparated
            } : null);
        } catch (error) {
            console.error('[Auth] Failed to fetch user groups:', error);
        }
    };

    // Update user profile when account changes
    useEffect(() => {
        if (BYPASS_AUTH) {
            if (manualLogout) {
                setUser(null);
                authManager.clearAuth();
            } else {
                // Create mock dev user
                const mockUser: UserProfile = {
                    id: DEV_EMAIL,
                    name: 'Dev User',
                    email: DEV_EMAIL,
                    tenantId: 'dev-tenant',
                    roles: ['dev'],
                    userGroups: [], // Will be populated by API call
                    groupNamesCommaSeparated: '' // Will be populated by API call
                };
                setUser(mockUser);
                authManager.setUserEmail(mockUser.email);
                setError(null);

                // Fetch user's groups from backend API even in bypass mode
                fetchAndSetUserGroups(mockUser.email);
            }
            setIsLoading(false);
            return; // Skip MSAL-driven logic
        }
        // Normal MSAL flow
        if (manualLogout) {
            setUser(null);
            authManager.clearAuth();
        } else if (isAuthenticated && account && !manualLogout) {
            const userProfile = convertAccountToUserProfile(account);
            setUser(userProfile);
            authManager.setUserEmail(userProfile.email);
            setError(null);

            // Fetch user's groups from backend API
            fetchAndSetUserGroups(userProfile.email);
        } else {
            setUser(null);
            authManager.clearAuth();
        }
        if (inProgress === InteractionStatus.None) setIsLoading(false);
    }, [BYPASS_AUTH, DEV_EMAIL, isAuthenticated, account, inProgress, manualLogout]);

    // Set MSAL instance for authenticated HTTP client
    useEffect(() => {
        setMsalInstance(instance);
    }, [instance]);

    // ── Idle timeout: auto-logout after 30 min of inactivity ──
    const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resetIdleTimer = useCallback(() => {
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
        idleTimerRef.current = setTimeout(() => {
            // Only auto-logout if currently authenticated
            if (!manualLogout) {
                console.info(`[Auth] Session timed out after ${SESSION_TIMEOUT_MS / 60000} minutes of inactivity`);
                logout();
            }
        }, SESSION_TIMEOUT_MS);
    }, [manualLogout]);

    useEffect(() => {
        // Only run idle timer when user is effectively authenticated
        if (!(BYPASS_AUTH ? !!user && !manualLogout : (isAuthenticated && !manualLogout))) {
            // Not authenticated — clear any running timer
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            return;
        }

        // Start idle timer and listen for activity
        resetIdleTimer();
        ACTIVITY_EVENTS.forEach(evt => window.addEventListener(evt, resetIdleTimer, { passive: true }));

        return () => {
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
            ACTIVITY_EVENTS.forEach(evt => window.removeEventListener(evt, resetIdleTimer));
        };
    }, [resetIdleTimer, isAuthenticated, manualLogout, user]);

    // Manual login method (triggered by "Get Started" button)
    const login = async (): Promise<void> => {
        if (BYPASS_AUTH) {
            // No-op in bypass mode
            setManualLogout(false);
            if (!user) {
                const mockUser: UserProfile = {
                    id: DEV_EMAIL,
                    name: 'Dev User',
                    email: DEV_EMAIL,
                    tenantId: 'dev-tenant',
                    roles: ['dev'],
                    userGroups: [],
                    groupNamesCommaSeparated: ''
                };
                setUser(mockUser);
                authManager.setUserEmail(mockUser.email);
                // Fetch user's groups from backend API
                fetchAndSetUserGroups(mockUser.email);
            }
            return;
        }
        try {
            setError(null);
            setIsLoading(true);

            // Clear manual logout flag when attempting login
            setManualLogout(false);

            // Use redirect for seamless experience
            await instance.loginRedirect(loginRequest);
        } catch (err: any) {
            console.error('[Auth] Login failed:', err);
            setError(err.message || 'Authentication failed. Please try again.');
            setIsLoading(false);
        }
    };

    // Logout method - clear session and stay in app
    const logout = async (): Promise<void> => {
        // Reset zoom to 100% so the landing page renders full-screen
        document.documentElement.style.zoom = '';
        document.documentElement.style.removeProperty('--app-zoom');
        document.documentElement.removeAttribute('data-zoom');

        if (BYPASS_AUTH) {
            setManualLogout(true);
            setUser(null);
            authManager.clearAuth();
            groupsFetchedForRef.current = null;
            return;
        }
        try {
            setError(null);
            setIsLoading(true);

            // Set manual logout flag first
            setManualLogout(true);

            // Clear all authentication state
            await instance.clearCache();

            // Clear auth manager
            authManager.clearAuth();
            groupsFetchedForRef.current = null;

            // Force clear our local state
            setUser(null);
            setIsLoading(false);
        } catch (err: any) {
            console.error('[Auth] Logout failed:', err);
            // Force clear state even on error
            setManualLogout(true);
            setUser(null);
            setError(null);
            setIsLoading(false);
        }
    };

    // Clear error state
    const clearError = () => setError(null);

    // Get current user's email for API calls
    const getUserEmail = (): string | null => {
        return user?.email || null;
    };

    // Effective authentication state (considers manual logout)
    const effectiveIsAuthenticated = (BYPASS_AUTH ? !!user && !manualLogout : (isAuthenticated && !manualLogout));

    const contextValue: AuthContextType = {
        isAuthenticated: effectiveIsAuthenticated,
        isLoading,
        user,
        login,
        logout,
        getUserEmail,
        error,
        clearError
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Export types
export type { AuthContextType };